import React, { useEffect, useState } from 'react';
import RequestList from './RequestList';
import useRequest from 'src/hooks/api/useRequest';
import Request, { RequestStatus } from 'src/interfaces/api/Request';
import { Order } from 'src/interfaces/Table';
import useSite from 'src/hooks/api/useSite';
import Site from 'src/interfaces/api/Site';
import useContact from 'src/hooks/api/useContact';
import Contact from 'src/interfaces/api/Contact';
import { unique } from 'src/utils/ArrayUtils';
import { useRecoilState } from 'recoil';
import sitesState from 'src/recoil/atoms/sites';

const RequestListContainer = (props) => {
  const { getAll } = useRequest();
  const { sites, getSites } = useSites();
  const { contacts, getContactsByCustomerAccess } = useContacts();

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RequestStatus>();
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState<Order>('desc');
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<Request[]>([]);

  useEffect(() => {
    if (!loading) {
      setError(undefined);
      setLoading(true);

      getAll('all', page + 1, limit, orderBy, order, statusFilter)
        .then((collection) => {
          setRequests(collection.items);
          setTotal(collection.total);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }
  }, [page, limit, orderBy, order, statusFilter]);

  useEffect(() => {
    const siteIds = requests.map((request) => request.siteId);
    getSites(siteIds);

    const creatorIds = requests.map((request) => request.creatorId);
    getContactsByCustomerAccess(creatorIds);
  }, [requests]);

  const handleFilterStatus = (status?: RequestStatus) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleClickRequest = (requestId: string) => {
    props.history.push(`/dashboard/requests/${requestId}`);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <RequestList
      loading={loading}
      requests={requests}
      sites={sites}
      contacts={contacts}
      total={total}
      page={page}
      rowsPerPage={limit}
      orderBy={orderBy}
      order={order}
      error={error}
      onClickRequest={handleClickRequest}
      onRequestSort={handleRequestSort}
      onFilterStatus={handleFilterStatus}
      onChangePage={handleChangePage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      {...props}
    />
  );
};

const useSites = () => {
  const { getBatch } = useSite();
  const [sites, setSites] = useRecoilState(sitesState);

  const getSites = (ids: string[]) => {
    const unknownSiteIds = getUnknownSiteIds(unique(ids));

    if (unknownSiteIds.length > 0) {
      getBatch(unknownSiteIds).then((collection) => {
        setSites((oldSites) => Object.assign({}, oldSites, ...collection.items.map((site) => ({ [site.id]: site }))));
      });
    }
  };

  const getUnknownSiteIds = (ids: Site['id'][]) => {
    const knownSiteIds = Object.keys(sites);
    return ids.filter((id) => knownSiteIds.indexOf(id) === -1);
  };

  return { sites, getSites };
};

const useContacts = () => {
  const { getByCustomerAccess: get } = useContact();
  const [contacts, setContacts] = useState<Contact[]>([]);

  const getContactsByCustomerAccess = (customerAccessIds: string[]) => {
    const unknownContactIds = getUnknownContactIds(unique(customerAccessIds));

    if (unknownContactIds.length > 0) {
      get(unknownContactIds).then((collection) => {
        setContacts(collection.items);
      });
    }
  };

  const getUnknownContactIds = (ids: string[]) => {
    const knownContactIds = contacts.map((contact) => contact.id);
    return ids.filter((id) => {
      return knownContactIds.indexOf(id) === -1;
    });
  };

  return { contacts, getContactsByCustomerAccess };
};

export default RequestListContainer;
